import { useCallback, useEffect, useRef, useState } from "react";
import { get } from "./api.ts";
import { useRefreshable } from "./refresh.ts";

export type Loadable<T> = {
  data: T | null;
  error: Error | null;
  loading: boolean;
  reload: () => Promise<void>;
};

/**
 * GET a path and keep the result. Stale data stays visible while reloading or after a failure, so
 * the page never blanks out; `error` says why the latest attempt failed.
 */
export function useApi<T>(path: string | null, opts: { pollMs?: number } = {}): Loadable<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(path !== null);
  const current = useRef(path);
  current.current = path;

  const reload = useCallback(async () => {
    if (!path) return;
    setLoading(true);
    try {
      const d = await get<T>(path);
      if (current.current === path) {
        setData(d);
        setError(null);
      }
    } catch (e) {
      if (current.current === path) setError(e as Error);
    } finally {
      if (current.current === path) setLoading(false);
    }
  }, [path]);

  // A pull at the top of the screen re-reads this, like any other loader on the page.
  useRefreshable(reload);

  useEffect(() => {
    setData(null);
    setError(null);
    void reload();
    if (!opts.pollMs) return;
    const t = setInterval(() => void reload(), opts.pollMs);
    return () => clearInterval(t);
  }, [reload, opts.pollMs]);

  return { data, error, loading, reload };
}

/** Browser connectivity, for the offline banner. */
export function useOnline(): boolean {
  const [online, setOnline] = useState(typeof navigator === "undefined" ? true : navigator.onLine);
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);
  return online;
}
